import { useState, useEffect, ReactNode, FC } from 'react';

import { data as en } from './translations/en-EN';
import { data as es } from './translations/es-ES';
import { data as de } from './translations/de-DE';
import { data as it } from './translations/it-IT';
import { data as fr } from './translations/fr-FR';
import { LocaleContext } from './context';
import { LocaleData } from '../../models/locale/locale-data';
import { Locales } from '../../models/locale/locales';
import { getTranslation } from '../../utils/translation/get';

const localePaths: Record<Locales, LocaleData> = {
  'de-DE': de,
  'en-EN': en,
  'es-ES': es,
  'fr-FR': fr,
  'it-IT': it,
};

// Default app locale set to 'de-DE'
const baseLocale: Locales = 'de-DE';

export const LocaleProvider: FC<{
  children: ReactNode;
  environment: 'sandbox' | 'development' | 'qa' | 'production';
}> = ({ children, environment = import.meta.env.MODE ?? 'production' }) => {
  const [locale, setLocaleState] = useState(getTranslation);
  const [data, setData] = useState<LocaleData | null>(null);

  const setLocale = (newLocale: Locales) => {
    setLocaleState(newLocale);
  };

  const isValidLocale = (locale: Locales): boolean => Object.keys(localePaths).includes(locale);

  useEffect(() => {
    const loadData = async () => {
      // Validate locale against locale paths list otherwise fallback to default
      const data = isValidLocale(locale) ? localePaths[locale] : localePaths[baseLocale];
      if (data) setData(data);
      else {
        if (environment !== 'production') {
          console.error(`Locale data for "${locale}" could not be loaded.`);
        }
      }
    };
    loadData();
  }, [locale]);

  return (
    <LocaleContext.Provider value={{ locale, setLocale, data }}>{children}</LocaleContext.Provider>
  );
};
